/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: modules/wireguard-client/WireGuardClientPage.tsx
 🗓️ Created: 2026-05-13
 🧩 Description:
    WireGuard VPN Client page for Ring2All PBX.
    Mirrors OpenVPNClientPage.tsx structure exactly.
────────────────────────────────────────────────────────────────*/

import React, { useState, useCallback, useRef } from 'react';
import {
    Upload, Wifi, WifiOff, RefreshCw,
    Trash2, Play, Pause, Server,
    ArrowUpDown, Clock, Network
} from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useWireGuardClient } from './hooks/useWireGuardClient';
import { ModuleFormHeader } from '../../components/layouts';
import { FormBox } from '../../components/ui/FormBox';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

function formatBytes(bytes: number): string {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(dateStr?: string | null): string {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
}

export default function WireGuardClientPage() {
    const { t } = useI18n();
    const {
        status,
        loading,
        uploading,
        connecting,
        uploadConfig,
        connect,
        disconnect,
        deleteConfiguration,
        refreshStatus,
    } = useWireGuardClient();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const content = event.target?.result as string;
                await uploadConfig(content);
            };
            reader.readAsText(file);
        }
        // Reset so the same file can be re-selected
        if (e.target) e.target.value = '';
    }, [uploadConfig]);

    const handleUploadClick = useCallback(() => { fileInputRef.current?.click(); }, []);
    const handleConnect     = useCallback(async () => { await connect(); }, [connect]);
    const handleDisconnect  = useCallback(async () => { await disconnect(); }, [disconnect]);
    const handleDeleteClick = useCallback(() => { setShowDeleteModal(true); }, []);
    const confirmDelete     = useCallback(async () => {
        setShowDeleteModal(false);
        await deleteConfiguration();
    }, [deleteConfiguration]);

    const isConnected = status?.connected ?? false;
    const hasConfig   = status?.hasConfig  ?? false;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="p-6 pb-20">
            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".conf"
                onChange={handleFileChange}
                className="hidden"
            />

            {/* ── Header — same structure as OpenVPNClientPage ── */}
            <ModuleFormHeader
                icon={<Network className="h-5 w-5 text-primary" />}
                title={t('wireguardClient.title', 'WireGuard VPN Client')}
                backButtonText=""
                onBack={() => {}}
                hideBackButton={true}
            >
                <div className="flex items-center gap-2">
                    {hasConfig ? (
                        <>
                            {/* Refresh */}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={refreshStatus}
                                disabled={connecting || uploading}
                                title={t('wireguardClient.actions.refresh', 'Refresh Status')}
                            >
                                <RefreshCw className="h-4 w-4" />
                            </Button>

                            {/* Connect / Disconnect */}
                            {isConnected ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleDisconnect}
                                    disabled={connecting}
                                >
                                    <Pause className="h-4 w-4 mr-2" />
                                    {t('wireguardClient.actions.disconnect', 'Disconnect')}
                                </Button>
                            ) : (
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={handleConnect}
                                    disabled={connecting}
                                >
                                    <Play className="h-4 w-4 mr-2" />
                                    {t('wireguardClient.actions.connect', 'Connect')}
                                </Button>
                            )}

                            {/* Re-import */}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleUploadClick}
                                disabled={uploading || connecting}
                            >
                                {uploading
                                    ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    : <Upload className="h-4 w-4 mr-2" />
                                }
                                {t('wireguardClient.actions.import', 'Import .conf')}
                            </Button>

                            {/* Delete */}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleDeleteClick}
                                disabled={connecting || uploading}
                                className="text-destructive hover:text-destructive"
                                title={t('wireguardClient.actions.delete', 'Delete Configuration')}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </>
                    ) : (
                        /* No config yet — primary upload button */
                        <Button
                            variant="default"
                            size="sm"
                            onClick={handleUploadClick}
                            disabled={uploading}
                        >
                            {uploading
                                ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                : <Upload className="h-4 w-4 mr-2" />
                            }
                            {t('wireguardClient.actions.uploadConfig', 'Upload Configuration')}
                        </Button>
                    )}
                </div>
            </ModuleFormHeader>

            {/* ── Main Content — mt-8 + space-y-4 like OpenVPNClientPage ── */}
            <div className="mt-8">
                {hasConfig ? (
                    <div className="space-y-4">
                        {/* Connection Status */}
                        <FormBox
                            title={t('wireguardClient.sections.status', 'VPN Tunnel Status')}
                            icon={<Wifi className="h-5 w-5" />}
                        >
                            {/* Status Banner */}
                            <div className={`p-3 rounded-lg border mb-4 ${isConnected ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-muted/50 border-muted'}`}>
                                <div className="flex items-center gap-3">
                                    {isConnected
                                        ? <Wifi className="h-6 w-6 text-emerald-500" />
                                        : <WifiOff className="h-6 w-6 text-muted-foreground" />
                                    }
                                    <div>
                                        <span className={`font-medium ${isConnected ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                                            {isConnected
                                                ? t('wireguardClient.status.connected', 'Connected')
                                                : t('wireguardClient.status.disconnected', 'Disconnected')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Connection Details — 4-column grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                                <Label className="text-left">
                                    {t('wireguardClient.fields.vpnIp', 'VPN IP')}
                                </Label>
                                <div className="font-mono font-medium">
                                    {status?.vpnIp || '-'}
                                </div>

                                <Label className="text-left">
                                    {t('wireguardClient.fields.serverEndpoint', 'SBC Endpoint')}
                                </Label>
                                <div className="font-mono font-medium">
                                    {status?.serverEndpoint || '-'}
                                </div>

                                <Label className="text-left">
                                    <Clock className="h-3.5 w-3.5 inline mr-1" />
                                    {t('wireguardClient.fields.lastHandshake', 'Last Handshake')}
                                </Label>
                                <div className="font-medium col-span-3">
                                    {formatDate(status?.lastHandshake)}
                                </div>

                                {isConnected && (
                                    <>
                                        <Label className="text-left">
                                            <ArrowUpDown className="h-3.5 w-3.5 inline mr-1" />
                                            {t('wireguardClient.fields.received', 'Bytes Received')}
                                        </Label>
                                        <div className="font-medium text-emerald-500">
                                            ↓ {formatBytes(status?.bytesReceived ?? 0)}
                                        </div>

                                        <Label className="text-left">
                                            {t('wireguardClient.fields.sent', 'Bytes Sent')}
                                        </Label>
                                        <div className="font-medium text-blue-500">
                                            ↑ {formatBytes(status?.bytesSent ?? 0)}
                                        </div>
                                    </>
                                )}
                            </div>
                        </FormBox>

                        {/* Configuration Info */}
                        <FormBox
                            title={t('wireguardClient.sections.configuration', 'Configuration')}
                            icon={<Server className="h-5 w-5" />}
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                                <Label className="text-left">
                                    {t('wireguardClient.fields.configStatus', 'Configuration')}
                                </Label>
                                <div className="font-medium col-span-3 flex items-center gap-2">
                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
                                        ✓ {t('wireguardClient.fields.configLoaded', 'Loaded')}
                                    </span>
                                </div>

                                <Label className="text-left">
                                    {t('wireguardClient.fields.protocol', 'Protocol')}
                                </Label>
                                <div className="font-medium col-span-3">
                                    WireGuard (UDP)
                                </div>
                            </div>
                        </FormBox>
                    </div>
                ) : (
                    /* No Configuration — centered upload prompt (same as OpenVPN) */
                    <FormBox
                        title={t('wireguardClient.sections.configuration', 'Configuration')}
                        icon={<Server className="h-5 w-5" />}
                    >
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="p-4 rounded-full bg-muted/50 mb-4">
                                <WifiOff className="h-12 w-12 text-muted-foreground/50" />
                            </div>
                            <h3 className="text-lg font-medium mb-2">
                                {t('wireguardClient.messages.noConfig', 'No configuration uploaded')}
                            </h3>
                            <p className="text-sm text-muted-foreground text-center max-w-md mb-6">
                                {t('wireguardClient.messages.noConfigDesc', 'Go to SBC Admin → Security → WireGuard Peers Manager, create a peer for this PBX, download the .conf file, and import it here.')}
                            </p>
                            <Button
                                variant="default"
                                onClick={handleUploadClick}
                                disabled={uploading}
                            >
                                {uploading
                                    ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    : <Upload className="h-4 w-4 mr-2" />
                                }
                                {t('wireguardClient.actions.uploadConfig', 'Upload Configuration')}
                            </Button>
                        </div>
                    </FormBox>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title={t('wireguardClient.confirmDelete.title', 'Delete WireGuard Configuration')}
                message={t('wireguardClient.confirmDelete.message', 'This will disconnect the VPN tunnel and remove the configuration file. The peer will remain registered in the SBC.')}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
            />
        </div>
    );
}
